import React, { useState, useRef, useCallback, useEffect, useLayoutEffect } from 'react';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useDictation } from '../../hooks/useDictation';

const SECTION_PLACEHOLDERS = {
  job_title: 'Welke functietitel heeft de vacature?',
  about_company: 'Vertel iets over het bedrijf...',
  job_description: 'Wat houdt de functie in?',
  requirements: 'Welke eisen stel je aan de kandidaat?',
  benefits: 'Wat bied je de kandidaat?',
  conditions: 'Wat zijn de arbeidsvoorwaarden?',
};

const SECTION_ORDER = [
  'job_title', 'about_company', 'job_description',
  'requirements', 'benefits', 'conditions',
];

function getPlaceholder(sections) {
  const filledCount = SECTION_ORDER.filter(k => sections[k]?.status !== 'empty').length;
  // First turn (nothing filled yet): invite a brain-dump, don't narrow to one field.
  if (filledCount === 0) return 'Vertel maar wat je in gedachten hebt, alles mag.';
  const nextEmpty = SECTION_ORDER.find(k => sections[k]?.status === 'empty');
  if (nextEmpty) return SECTION_PLACEHOLDERS[nextEmpty];
  return 'Nog iets aanpassen?';
}

const ACCEPTED_TYPES = '.txt,.pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain';

export default function InputBar({
  onSendText,
  onUploadFile,
  onStartVoice,
  onStopVoice,
  voiceStatus,
  isSpeaking,
}) {
  const { state } = useWorkspace();
  const [text, setText] = useState('');
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const interimRef = useRef(null);
  const dictationBaseRef = useRef('');

  // Auto-grow textarea between 1 line (44px) and 4 lines (~100px). Reset to
  // 'auto' first so shrinking on backspace/clear works. Wrap in rAF so the
  // measurement reads the *post-render* scrollHeight, not the stale one.
  const resizeTextarea = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const next = Math.max(44, Math.min(el.scrollHeight, 100));
    el.style.height = next + 'px';
  }, []);

  const dictation = useDictation({
    lang: 'nl-NL',
    onResult: (finalTranscript) => {
      // Append finalized transcript to whatever was typed before dictation started.
      const base = dictationBaseRef.current;
      const combined = base ? `${base} ${finalTranscript}`.replace(/\s+/g, ' ').trim() : finalTranscript;
      setText(combined);
    },
  });

  const isVoiceConnected = voiceStatus === 'connected';
  const isOrbActive = isVoiceConnected && !isSpeaking;

  // Trigger resize whenever `text` changes. useLayoutEffect runs AFTER the DOM
  // is mutated but BEFORE the browser paints, so scrollHeight is the fresh
  // post-update value, never stale. Plain useEffect ran AFTER paint, which
  // sometimes saw the previous height when state changes batched rapidly
  // (e.g. multiple final-transcripts arriving during dictation).
  useLayoutEffect(() => {
    resizeTextarea();
  }, [text, resizeTextarea]);

  // Keep the interim chip scrolled to the bottom so the latest spoken words
  // are always visible. Without this, a long monologue would scroll past the
  // visible area and the user would stare at the START of their utterance.
  useEffect(() => {
    if (interimRef.current) {
      interimRef.current.scrollTop = interimRef.current.scrollHeight;
    }
  }, [dictation.interimTranscript]);

  const submittingRef = useRef(false);
  const handleSubmit = (e) => {
    e?.preventDefault?.();
    // Submit-guard: same input cannot fire twice in rapid succession
    // (form-onSubmit + button-click + Enter-keydown all collapse to one send).
    if (submittingRef.current) return;
    const trimmed = text.trim();
    if (!trimmed) return;
    submittingRef.current = true;
    onSendText(trimmed);
    setText('');
    requestAnimationFrame(() => {
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
    });
    // Re-allow after 400ms, covers any synthetic event chain
    setTimeout(() => { submittingRef.current = false; }, 400);
  };

  const handleKeyDown = (e) => {
    // Enter sends; Shift+Enter inserts newline.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleVoiceClick = () => {
    if (isVoiceConnected) {
      onStopVoice();
    } else {
      if (dictation.isListening) dictation.stop();
      onStartVoice();
    }
  };

  const handleDictationClick = () => {
    if (dictation.isListening) {
      dictation.stop();
      return;
    }
    // Capture whatever's in the textarea right now as the base, dictated
    // text gets appended to it rather than replacing it.
    dictationBaseRef.current = text.trim();
    dictation.reset();
    if (isVoiceConnected) onStopVoice();
    dictation.start();
    requestAnimationFrame(() => textareaRef.current?.focus());
  };

  // Clear-on-submit needs to reset dictation buffer too, otherwise the next
  // dictation session would replay the last finalized transcript.
  useEffect(() => {
    if (!text) dictationBaseRef.current = '';
  }, [text]);

  const handleFileChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file && onUploadFile) {
      onUploadFile(file);
    }
    // Reset input so same file can be uploaded again
    e.target.value = '';
  }, [onUploadFile]);

  return (
    <form className="input-bar" onSubmit={handleSubmit}>
      <button
        type="button"
        className={`voice-orb ${isOrbActive ? 'voice-orb--active' : ''}`}
        onClick={handleVoiceClick}
        aria-label={isVoiceConnected ? 'Pauzeer spraak' : 'Start spraak'}
      >
        <span className="voice-orb__icon material-icons">
          {isVoiceConnected ? 'pause' : 'mic'}
        </span>
      </button>

      <span className="input-bar__divider">of</span>

      <div className="input-bar__text-wrap">
        {dictation.isListening && dictation.interimTranscript && (
          <div ref={interimRef} className="input-bar__interim" aria-live="polite">
            {dictation.interimTranscript}
          </div>
        )}
        <textarea
          ref={textareaRef}
          className={`input-bar__text ${dictation.isListening ? 'input-bar__text--dictating' : ''}`}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            if (dictation.isListening) dictationBaseRef.current = e.target.value.trim();
          }}
          onKeyDown={handleKeyDown}
          rows={1}
          placeholder={dictation.isListening ? 'Luisteren... begin maar te praten' : getPlaceholder(state.document.sections)}
          aria-label="Bericht invoeren"
        />
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_TYPES}
        onChange={handleFileChange}
        style={{ display: 'none' }}
        aria-hidden="true"
      />

      {dictation.isSupported && (
        <button
          type="button"
          className={`input-bar__dictate ${dictation.isListening ? 'input-bar__dictate--active' : ''}`}
          onClick={handleDictationClick}
          aria-label={dictation.isListening ? 'Stop met dicteren' : 'Start dicteren'}
          title={dictation.isListening ? 'Stop met dicteren' : 'Dicteer je tekst, je woorden komen direct in het invoerveld'}
        >
          <span className="material-icons">
            {dictation.isListening ? 'graphic_eq' : 'record_voice_over'}
          </span>
        </button>
      )}

      <button
        type="button"
        className="input-bar__upload"
        onClick={() => fileInputRef.current?.click()}
        aria-label="Bestand uploaden"
        title="Upload een bestand (PDF, DOCX, TXT)"
      >
        <span className="material-icons">attach_file</span>
      </button>

      <button
        type="submit"
        className="input-bar__send"
        disabled={!text.trim()}
        aria-label="Verstuur bericht"
      >
        <span className="material-icons">send</span>
      </button>
    </form>
  );
}
